/**
 * Per-assistant-message action bar: copy, thumbs-up, thumbs-down.
 *
 * Mirrors chat.js's feedback UX:
 *   - thumbs-up        → fire-and-forget positive feedback, brief confirm.
 *   - thumbs-down      → reveal an inline comment form; submit (or skip) files
 *                        negative feedback with the optional note.
 * Once a rating is chosen the buttons lock (no double-vote), matching legacy.
 *
 * Feedback is submitted through the data-access seam (useSubmitFeedback →
 * api.post(?action=feedback)). The user/assistant message pair is read from the
 * live draft list by index so the backend learning issue has full context.
 */
import {
  ArrowClockwiseIcon,
  CheckIcon,
  CopyIcon,
  ThumbsDownIcon,
  ThumbsUpIcon,
} from '@phosphor-icons/react';
import { useState } from 'react';
import { useChat } from '@/features/chat/data-access/useChat';
import { useSubmitFeedback } from '@/features/chat/data-access/useConversations';
import { useSessionStore } from '@/features/chat/store/sessionStore';
import { Button, Textarea } from '@andavo/web-ui';

type Props = {
  /** Index of this assistant message in the draft list. */
  messageIndex: number;
  /** The assistant message text (for copy + feedback payload). */
  content: string;
  /**
   * True when this is the last assistant message in the thread — only then is
   * the Regenerate action offered (it re-streams from the prior user turn,
   * replacing this reply).
   */
  isLastAssistant?: boolean;
};

type Rating = 'up' | 'down' | null;

export function MessageActions({
  messageIndex,
  content,
  isLastAssistant = false,
}: Props) {
  const submitFeedback = useSubmitFeedback();
  const { regenerate, isStreaming } = useChat();
  const activeConversationId = useSessionStore((s) => s.activeConversationId);
  const [copied, setCopied] = useState(false);
  const [rating, setRating] = useState<Rating>(null);
  const [showForm, setShowForm] = useState(false);
  const [comment, setComment] = useState('');
  const [thanks, setThanks] = useState(false);

  const messagePair = () => {
    const draft = useSessionStore.getState().draftMessages;
    const assistant = draft[messageIndex]?.content ?? content;
    const user =
      messageIndex > 0 ? (draft[messageIndex - 1]?.content ?? '') : '';
    return { user, assistant };
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable (insecure context) — silent, matches legacy */
    }
  };

  const fileFeedback = (r: 'up' | 'down', note?: string) => {
    const { user, assistant } = messagePair();
    submitFeedback.mutate({
      conversation_id: activeConversationId,
      rating: r,
      comment: note || undefined,
      user_message: user ? user.slice(0, 500) : undefined,
      assistant_message: assistant ? assistant.slice(0, 1000) : undefined,
    });
  };

  const onUp = () => {
    if (rating) return;
    setRating('up');
    fileFeedback('up');
    setThanks(true);
    setTimeout(() => setThanks(false), 2000);
  };

  const onDown = () => {
    if (rating) return;
    setRating('down');
    setShowForm(true);
  };

  const closeForm = (note: string) => {
    fileFeedback('down', note);
    setShowForm(false);
    setThanks(true);
    setTimeout(() => setThanks(false), 2500);
  };

  return (
    <div className="mt-2 flex flex-col gap-2">
      <div className="flex items-center gap-1">
        <Button
          aria-label="Copy response"
          className="h-7 gap-1 px-2 text-content-secondary"
          onClick={() => {
            void copy();
          }}
          size="sm"
          variant="ghost"
        >
          {copied ? (
            <CheckIcon className="size-icon-sm" />
          ) : (
            <CopyIcon className="size-icon-sm" />
          )}
          <span className="text-xs">{copied ? 'Copied' : 'Copy'}</span>
        </Button>

        <Button
          aria-label="Good response"
          // data-testid="feedback-thumb-up" — e2e feedback spec thumbs-up
          // (selectors.ts feedbackThumbUp).
          data-testid="feedback-thumb-up"
          aria-pressed={rating === 'up'}
          className={
            rating === 'up'
              ? 'h-7 px-2 text-content-action'
              : 'h-7 px-2 text-content-secondary'
          }
          disabled={rating !== null}
          onClick={onUp}
          size="sm"
          variant="ghost"
        >
          <ThumbsUpIcon
            className="size-icon-sm"
            weight={rating === 'up' ? 'fill' : 'regular'}
          />
        </Button>

        <Button
          aria-label="Bad response"
          // data-testid="feedback-thumb-down" — e2e feedback spec thumbs-down
          // (selectors.ts feedbackThumbDown); reveals the comment form below.
          data-testid="feedback-thumb-down"
          aria-pressed={rating === 'down'}
          className={
            rating === 'down'
              ? 'h-7 px-2 text-content-action'
              : 'h-7 px-2 text-content-secondary'
          }
          disabled={rating !== null}
          onClick={onDown}
          size="sm"
          variant="ghost"
        >
          <ThumbsDownIcon
            className="size-icon-sm"
            weight={rating === 'down' ? 'fill' : 'regular'}
          />
        </Button>

        {isLastAssistant && (
          <Button
            aria-label="Regenerate response"
            // data-testid="message-regenerate" — the regenerate action the e2e
            // chat spec clicks on the last assistant reply (selectors.ts
            // messageRegenerate). Re-streams from the prior user turn.
            data-testid="message-regenerate"
            className="h-7 gap-1 px-2 text-content-secondary"
            disabled={isStreaming}
            onClick={() => {
              void regenerate();
            }}
            size="sm"
            variant="ghost"
          >
            <ArrowClockwiseIcon className="size-icon-sm" />
            <span className="text-xs">Regenerate</span>
          </Button>
        )}
      </div>

      {showForm && (
        // data-testid="feedback-form" — e2e feedback spec asserts this reveals
        // on thumbs-down (selectors.ts TESTID.feedbackForm).
        <div
          className="flex flex-col gap-2 rounded-md border border-border-subtle bg-surface-foreground p-3"
          data-testid="feedback-form"
        >
          <Textarea
            autoFocus
            className="min-h-[44px] resize-none"
            // data-testid="feedback-comment" — the negative-feedback note input
            // (selectors.ts TESTID.feedbackComment).
            data-testid="feedback-comment"
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                closeForm(comment.trim());
              }
            }}
            placeholder="What was wrong with this response? (optional)"
            rows={2}
            value={comment}
          />
          <div className="flex justify-end gap-2">
            <Button onClick={() => closeForm('')} size="sm" variant="outline">
              Skip
            </Button>
            <Button
              // data-testid="feedback-submit" — submit the negative-feedback
              // note (selectors.ts TESTID.feedbackSubmit).
              data-testid="feedback-submit"
              onClick={() => closeForm(comment.trim())}
              size="sm"
            >
              Submit
            </Button>
          </div>
        </div>
      )}

      {thanks && (
        <span className="font-inter text-content-secondary text-xs">
          Thanks for your feedback
        </span>
      )}
    </div>
  );
}
